import { Card, Col, DatePicker, Form, Radio, Row } from 'antd'
import { FormInstance } from 'antd/lib'

import { useClassifierServiceGetCommonClassifierElementListEndpoint } from 'src/api/queries'
import { ClassifierCategory, ClassifierType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { OpportunisticDiseaseInfiniteSelect } from 'src/features/persons/components/PersonConsultations/OpportunisticDiseaseInfiniteSelect'
import { useClassifierId } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  form: FormInstance
}

export const EpidemiologyPrimaryDiagnosticCard = ({ form }: Props) => {
  const { t, currentLang } = useLocales()

  const classifierId = useClassifierId(
    ClassifierType.EPIDEMIOLOGY_CASE_CLASSIFICATION,
    ClassifierCategory.TREATMENT
  )

  const { data: commonElements } =
    useClassifierServiceGetCommonClassifierElementListEndpoint(
      { id: String(classifierId) },
      [classifierId],
      { enabled: !!classifierId }
    )

  return (
    <Card title={t('epidemiology.primary_diagnostic')}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Form.Item
            name={['form', 'primaryDiagnostic', 'diagnoses']}
            label={t('epidemiology.diagnoses_title')}
            rules={requiredRules}
          >
            <OpportunisticDiseaseInfiniteSelect
              fieldName={['form', 'primaryDiagnostic', 'diagnoses']}
              form={form}
              valueShape="full"
              placeholder={t('common.select_from_list')}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['form', 'primaryDiagnostic', 'diagnosisDate']}
            label={t('epidemiology.diagnostic_date_time')}
            rules={requiredRules}
          >
            <DatePicker
              className="full-width"
              showTime
              showSecond={false}
              needConfirm={false}
              format={dayMonthYearHourMinutesFormatWithDot}
              disabledDate={disabledFutureDate}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['form', 'primaryDiagnostic', 'dateOfDiseaseOnset']}
            label={t('epidemiology.date_time_of_disease_onset')}
            rules={requiredRules}
          >
            <DatePicker
              className="full-width"
              showTime
              showSecond={false}
              needConfirm={false}
              format={dayMonthYearHourMinutesFormatWithDot}
              disabledDate={disabledFutureDate}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name={['form', 'primaryDiagnostic', 'classificationLevel']}
            label={t('epidemiology.classification_level')}
            rules={requiredRules}
            getValueProps={(val) => ({ value: val?.id })}
            getValueFromEvent={(e) => {
              const id = e?.target?.value
              if (!id) return undefined
              const found = commonElements?.data?.find((o) => o.id === id)

              return found ? { id: found.id, name: found.translation } : { id }
            }}
          >
            <Radio.Group>
              {commonElements?.data?.map((item) => (
                <Radio key={item.id} value={item.id}>
                  {item.translation?.[currentLang.value]}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  )
}
